/** @file Animations panel: the list (open, create, rename, length, duplicate, delete) and mix preview. */
import type { IDockviewPanelProps } from 'dockview-react';
import type { CommandCall } from '@pose2d/format/types';
import { useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { editorDeps } from '../../playback/editorDeps';
import { playhead } from '../../playback/playhead';
import { errorText } from '../../shell/i18n/errorText';
import { useT } from '../../shell/i18n/useT';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';
import { toolStore } from '../../stores/toolStore';
import { TextInput } from '../../ui-kit/TextInput';
import { animationCalls } from './animationCalls';
import { AnimationRow } from './AnimationRow';
import { animationRows } from './animationRows';
import { copyName } from './copyName';
import { MixPreview } from './MixPreview';
import './animations.css';

/** Opening an animation switches to Animate mode (spec §9: animations are edited there). */
function open(id: string): void {
  playhead.setAnimation(id);
  toolStore.setState({ mode: 'animate' });
}

/** Lists the working document's animations (W3-10); every edit is one command through the editor dispatch. */
export function AnimationsPanel({ api }: IDockviewPanelProps) {
  const t = useT();
  const doc = useStore(docStore, currentDoc);
  const current = useStore(playheadStore, (s) => s.animation);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const deps = useMemo(() => editorDeps((thrown) => { setFailure({ thrown }); }), []);
  const rows = useMemo(() => (doc === null ? [] : animationRows(doc)), [doc]);
  if (doc === null) return null;
  const names = rows.map((r) => r.name);
  const send = (call: CommandCall): void => { deps.dispatch([call]); };
  const remove = (id: string): void => {
    if (id === current) playhead.setAnimation(null);
    send(animationCalls.remove(id));
  };
  return (
    <div className="animations-panel" data-testid={`panel-${api.id}`}>
      {failure === null ? null : <p role="alert" className="animations-error">{errorText(t, failure.thrown)}</p>}
      <ul className="animations-list" aria-label={t('panels.animations.list')}>
        {rows.map((row) => (
          <AnimationRow key={row.id} row={row} current={row.id === current} copy={copyName(row.name, names)} t={t} send={send} onOpen={() => { open(row.id); }} onRemove={() => { remove(row.id); }} />
        ))}
      </ul>
      {rows.length === 0 ? <p className="animations-empty">{t('panels.animations.empty')}</p> : null}
      <TextInput value="" label={t('panels.animations.newName')} placeholder={t('panels.animations.newName')} onCommit={(name) => { send(animationCalls.create(name)); }} />
      <MixPreview doc={doc} rows={rows} deps={deps} t={t} />
    </div>
  );
}
